import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { navLinks } from '../../../mocks/bbenergy';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    const id = href.substring(1);
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const navbarHeight = 160;
      const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0c1f38] shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-40">
        {/* Logo */}
        <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="flex items-center cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/45dd2226-2172-4d0f-be13-18af06899281/e1672e9c-78e3-4990-8241-00392eb151b9_nrgdark-logo.png?v=d3e82d71346a99a9772a7ff721b32df6"
            alt="NRG Logo"
            className="h-36 w-auto object-contain brightness-0 invert"
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <div
              key={link.label}
              className="relative"
              onMouseEnter={() => link.children && setActiveDropdown(link.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <a
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-white/90 hover:text-white text-sm font-medium tracking-wide transition-colors duration-200 flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                {link.label}
                {link.children && <i className="ri-arrow-down-s-line text-xs" />}
              </a>
              {link.children && activeDropdown === link.label && (
                <div className="absolute top-full left-0 mt-2 bg-[#0c1f38] min-w-[180px] rounded-md overflow-hidden shadow-xl border border-white/10">
                  {link.children.map((child) => (
                    <a
                      key={child}
                      href="#"
                      className="block px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {child}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <button className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white cursor-pointer">
            <i className="ri-search-line text-lg" />
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-white cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0c1f38] border-t border-white/10 px-6 pb-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="block py-3 text-white/80 hover:text-white text-sm font-medium border-b border-white/10 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </nav>
  );
}
